import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, CircularProgress, Alert, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Button, Card, CardContent,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Snackbar
} from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { dppService } from '../services/dppService';

function DPPIssuance() {
  const [metadata, setMetadata] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, PENDING: 0, VERIFIED: 0, ISSUED: 0, REJECTED: 0, FLAGGED: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  // Issuance state
  const [issuing, setIssuing] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  // Detail dialog (row click)
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadData = useCallback(async () => {
    try {
      const [metaResult, statsResult] = await Promise.all([
        dppService.getMetadataList({ status: statusFilter || undefined, limit: 100 }),
        dppService.getStats(),
      ]);
      setMetadata(metaResult.metadata || []);
      setTotal(metaResult.total || 0);
      setStats(statsResult ?? { total: 0, PENDING: 0, VERIFIED: 0, ISSUED: 0, REJECTED: 0, FLAGGED: 0 });
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      dppService.getMetadataList({ status: statusFilter || undefined, limit: 100 })
        .then(r => { setMetadata(r.metadata || []); setTotal(r.total || 0); })
        .catch(() => {});
      dppService.getStats().then(r => setStats(r ?? { total: 0, PENDING: 0, VERIFIED: 0, ISSUED: 0, REJECTED: 0, FLAGGED: 0 })).catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [statusFilter]);

  const handleIssueDPP = async (productFootprintId) => {
    try {
      setIssuing(productFootprintId);
      const result = await dppService.issueDPP(productFootprintId);
      setSnackbar({ open: true, message: `DPP issued for batch ${result.batch_id}`, severity: 'success' });
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: `Issuance failed: ${err.response?.data?.error?.message || err.message}`, severity: 'error' });
    } finally {
      setIssuing(null);
    }
  };

  const handleViewQR = async (batchId) => {
    try {
      setQrLoading(true);
      setQrDialogOpen(true);
      const data = await dppService.getQRCodeData(batchId);
      setQrData(data);
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to load QR data: ${err.message}`, severity: 'error' });
    } finally {
      setQrLoading(false);
    }
  };

  // Row click: show the verification & issuance detail, centered on the dVE
  // session record. The list row renders immediately; the full metadata
  // detail (verification_result JSONB, principle checks, NC ids) replaces it
  // once loaded.
  const handleRowClick = async (row) => {
    setDetail(row);
    setDetailOpen(true);
    try {
      const full = await dppService.getMetadataDetail(row.product_footprint_id);
      if (full) setDetail(full);
    } catch {
      // keep the list-row data if the detail fetch fails
    }
  };

  const getStatusChip = (status) => {
    const config = {
      PENDING: { color: 'warning', label: 'Awaiting Verification' },
      VERIFIED: { color: 'success', label: 'Verified' },
      ISSUED: { color: 'info', label: 'Issued' },
      REJECTED: { color: 'error', label: 'Rejected' },
      FLAGGED: { color: 'warning', label: 'Flagged' },
    };
    const c = config[status] || { color: 'default', label: status };
    return <Chip label={c.label} size="small" color={c.color} />;
  };

  const statCards = [
    { label: 'Total DPPs', value: stats.total, color: 'primary' },
    { label: 'Pending', value: stats.PENDING, color: 'warning' },
    { label: 'Verified (Ready)', value: stats.VERIFIED, color: 'success' },
    { label: 'Issued', value: stats.ISSUED, color: 'info' },
  ];

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>DPP Issuance</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Step 4: Review dVE verification results and issue final Digital Product Passports with QR codes.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((card) => (
          <Grid item xs={6} md={3} key={card.label}>
            <Card>
              <CardContent>
                <Typography variant="h4" color={`${card.color}.main`}>{card.value}</Typography>
                <Typography variant="body2" color="text.secondary">{card.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Verification Status</InputLabel>
              <Select
                value={statusFilter}
                label="Verification Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="VERIFIED">Verified</MenuItem>
                <MenuItem value="ISSUED">Issued</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
                <MenuItem value="FLAGGED">Flagged</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography variant="body2" color="text.secondary">
              {total} DPP metadata record(s). Auto-refreshes every 15 seconds. Click a row for its dVE verification session record.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product Name</TableCell>
              <TableCell>Batch ID</TableCell>
              <TableCell>Verification Status</TableCell>
              <TableCell>dVE DID</TableCell>
              <TableCell>EU Registry</TableCell>
              <TableCell>Data Carrier</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {metadata.map((row) => (
              <TableRow
                key={row.product_footprint_id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => handleRowClick(row)}
              >
                <TableCell>{row.product_name_company || row.product_footprint_id}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.batch_id}</TableCell>
                <TableCell>{getStatusChip(row.verification_status)}</TableCell>
                <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.dve_did || '-'}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {row.eu_registry_entry_id || '-'}
                </TableCell>
                <TableCell>{row.data_carrier_type}</TableCell>
                <TableCell align="right">
                  {row.verification_status === 'VERIFIED' && (
                    <Button
                      variant="contained" size="small" startIcon={<CheckCircleIcon />}
                      onClick={(e) => { e.stopPropagation(); handleIssueDPP(row.product_footprint_id); }}
                      disabled={issuing === row.product_footprint_id}
                    >
                      {issuing === row.product_footprint_id ? 'Issuing...' : 'Issue DPP'}
                    </Button>
                  )}
                  {row.verification_status === 'ISSUED' && (
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewQR(row.batch_id); }} title="View QR Code">
                      <QrCode2Icon />
                    </IconButton>
                  )}
                  {row.verification_status === 'PENDING' && (
                    <Typography variant="caption" color="text.secondary">Awaiting dVE</Typography>
                  )}
                  {row.verification_status === 'REJECTED' && (
                    <Typography variant="caption" color="error">Rejected</Typography>
                  )}
                  {row.verification_status === 'FLAGGED' && (
                    <Typography variant="caption" color="warning">Needs review</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {metadata.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No DPP metadata records. Submit DPP claims from the Compilation page first.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Verification & Issuance Detail Dialog (row click) */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          DPP Verification Record
          {detail?.verification_status && getStatusChip(detail.verification_status)}
        </DialogTitle>
        <DialogContent dividers>
          {!detail ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          ) : (
            <Grid container spacing={2}>
              {/* Product & batch */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Product</Typography>
                <Typography variant="body1">{detail.product_name_company || detail.product_footprint_id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Batch ID</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{detail.batch_id}</Typography>
              </Grid>

              {/* dVE verification session record */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">dVE Verification Session</Typography>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Session ID</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {detail.verification_session_id || '\u2014 (no dVE session recorded)'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Verified At (dVE)</Typography>
                      <Typography variant="body2">
                        {detail.dve_verified_at ? new Date(detail.dve_verified_at).toLocaleString() : '\u2014'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Confidence</Typography>
                      <Typography variant="body2">
                        {detail.verification_result?.confidence_score != null
                          ? `${Math.round(detail.verification_result.confidence_score * 100)}%`
                          : '\u2014'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">dVE DID</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {detail.dve_did || '\u2014'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">dVE VC ID</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{detail.dve_vc_id || '\u2014'}</Typography>
                    </Grid>
                    {detail.verification_result?.approval_id && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">dVE Approval ID</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {detail.verification_result.approval_id}
                        </Typography>
                      </Grid>
                    )}
                    {Array.isArray(detail.verification_result?.non_conformance_ids)
                      && detail.verification_result.non_conformance_ids.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Non-Conformance IDs</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {detail.verification_result.non_conformance_ids.map((id) => (
                            <Chip key={id} label={id} size="small" color="warning" sx={{ fontFamily: 'monospace' }} />
                          ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {/* dVE reasoning */}
              {detail.verification_result?.reasoning && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">dVE Reasoning</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 200, overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {detail.verification_result.reasoning}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Principle checks */}
              {Array.isArray(detail.verification_result?.principle_checks)
                && detail.verification_result.principle_checks.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Principle Checks</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {detail.verification_result.principle_checks.map((pc, i) => (
                      <Chip
                        key={i}
                        size="small"
                        color={pc.passed === false ? 'error' : 'success'}
                        label={pc.principle || pc.name || `Principle ${i + 1}`}
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Issuance & tracking */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">EU Registry Entry</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{detail.eu_registry_entry_id || '\u2014'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Data Carrier</Typography>
                <Typography variant="body2">{detail.data_carrier_type || '\u2014'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Metadata Created</Typography>
                <Typography variant="body2">{detail.created_at ? new Date(detail.created_at).toLocaleString() : '\u2014'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Last Updated</Typography>
                <Typography variant="body2">{detail.updated_at ? new Date(detail.updated_at).toLocaleString() : '\u2014'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>DPP Data Carrier</DialogTitle>
        <DialogContent>
          {qrLoading ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          ) : qrData ? (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Batch ID</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{qrData.batch_id}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Carrier Type</Typography>
                  <Typography variant="body1">{qrData.data_carrier_type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Verification</Typography>
                  {getStatusChip(qrData.verification_status)}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Data Carrier URL</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {qrData.data_carrier_url}
                  </Typography>
                </Grid>
                {qrData.dve_did && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">dVE DID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {qrData.dve_did}
                    </Typography>
                  </Grid>
                )}
                {qrData.dve_vc_id && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">dVE VC ID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{qrData.dve_vc_id}</Typography>
                  </Grid>
                )}
                {qrData.eu_registry_entry_id && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">EU Registry Entry</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{qrData.eu_registry_entry_id}</Typography>
                  </Grid>
                )}
                {qrData.product && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Product Summary</Typography>
                    <Paper variant="outlined" sx={{ p: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      <pre>{JSON.stringify(qrData.product, null, 2)}</pre>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : (
            <Typography>No QR data found</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}

export default DPPIssuance;
